// Refactored HttpAuthInterceptor (clean, modular, same behavior)
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ModalComponent } from '../components/modal/modal.component';
import { NotificationService } from '../services/notification.service';

@Injectable({ providedIn: 'root' })
export class HttpAuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private modal: NgbModal,
    private notification: NotificationService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let updatedRequest = request;

    updatedRequest = this.applyApimKey(updatedRequest);
    updatedRequest = this.applyAuthToken(updatedRequest);
    updatedRequest = this.applyCustomHeaders(updatedRequest);
    updatedRequest = this.applySpecialServiceHeaders(updatedRequest);

    return next.handle(updatedRequest).pipe(
      map(event => this.handleResponse(event, updatedRequest)),
      catchError(error => this.handleError(error, updatedRequest))
    );
  }

  // -------------------------------------------------------------------------
  // HEADER MODULES
  // -------------------------------------------------------------------------

  private applyApimKey(request: HttpRequest<any>): HttpRequest<any> {
    if (!environment.apimKey) return request;
    return request.clone({ setHeaders: { 'Ocp-Apim-Subscription-Key': environment.apimKey } });
  }

  private applyAuthToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = request.url.includes(environment.api + '/refresh')
      ? sessionStorage.getItem('refreshtoken')
      : sessionStorage.getItem('auth');

    const isMainApi = request.url.includes(environment.api);

    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: isMainApi
    });
  }

  private applySpecialServiceHeaders(request: HttpRequest<any>): HttpRequest<any> {
    let headers = {} as any;

    if (request.url.includes(environment.harmonyBaseURL)) {
      const basic = btoa(`${environment.harmonyUser}:${environment.harmonyPassword}`);
      headers['Authorization'] = `Basic ${basic}`;
    }

    if (request.url.includes(environment.rules_json_url)) {
      return request.clone({ headers: request.headers.delete('Authorization') });
    }

    return request.clone({ setHeaders: headers });
  }

  private applyCustomHeaders(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'company-brand': environment.company_brand,
        'provider-id': sessionStorage.getItem('provider') || '',
        'Cache-Control': 'no-cache',
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
  }

  // -------------------------------------------------------------------------
  // RESPONSE HANDLING
  // -------------------------------------------------------------------------

  private handleResponse(event: HttpEvent<any>, request: HttpRequest<any>): HttpEvent<any> {
    if (!(event instanceof HttpResponse)) return event;

    if (!environment.apimKey) return event;

    const isStringBody = typeof event.body === 'string';

    if (request.url.includes('address-lookup') && isStringBody) {
      return event.clone({ body: JSON.parse(event.body) });
    }

    if (this.isApimUrl(request) && environment.payload_encrypt === 'true' && isStringBody) {
      return event.clone({ body: JSON.parse(event.body) });
    }

    return event;
  }

  // -------------------------------------------------------------------------
  // ERROR HANDLING
  // -------------------------------------------------------------------------

  private handleError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    let errorMsg = error.error;

    if (this.shouldParseEncryptedError(request, error)) {
      try {
        errorMsg = JSON.parse(errorMsg);
      } catch {}
    }

    switch (error.status) {
      case 401:
        this.handle401(request);
        break;

      case 403:
        this.handle403(errorMsg);
        break;

      case 404:
        this.router.navigate(['/error', 404]);
        break;

      case 422:
        this.handle422(errorMsg);
        break;

      case 500:
        this.notification.error(this.extractMessage(errorMsg) || error.message, 'Error');
        break;

      case 503:
        this.router.navigate(['/error', 503]);
        break;

      default:
        this.notification.error(
          'The Portal has encountered an error. Please log back in to continue.',
          'Application Error'
        );
        break;
    }

    return throwError(() => error);
  }

  private shouldParseEncryptedError(request: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return (
      !!environment.apimKey &&
      this.isApimUrl(request) &&
      environment.payload_encrypt === 'true' &&
      typeof error.error === 'string'
    );
  }

  private handle401(request: HttpRequest<any>): void {
    if (request.headers.has('sliqpay-external')) return;
    this.openSessionTimedOutDialog();
  }

  private handle403(errorMsg: any): void {
    const errors = typeof errorMsg === 'string' ? JSON.parse(errorMsg).errors : errorMsg.errors;
    const err = errors[0];

    const modalRef = this.modal.open(ModalComponent, {
      size: 'sm',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.data = {
      title: 'Access Error',
      closeBtn: true,
      content: err.message || 'Access denied. Please contact your administrator.',
      buttons: [{ text: 'Log in', class: 'btn-primary', value: 'ok' }]
    };

    modalRef.closed.pipe(take(1)).subscribe(() => {
      sessionStorage.clear();
      this.router.navigateByUrl('/login');
    });
  }

  private handle422(errorMsg: any): void {
    const errors = typeof errorMsg === 'string' ? JSON.parse(errorMsg).errors : errorMsg.errors;
    const err = errors[0];

    // TO DO: Add an error code for this
    if (err.message === 'You do not have access to this system') {
      sessionStorage.clear();
      this.router.navigateByUrl('/login');
      return;
    }

    if (err.isRedirect) {
      this.modal.dismissAll();
      sessionStorage.setItem(
        'errorRedirection',
        JSON.stringify({ message: err.message, errorCode: err.code })
      );
      this.router.navigate(['payment-sms-journey/error']);
      return;
    }

    if (err.code === 'OTP_MAX_ATTEMPT') {
      this.openErrorModal(err.title || 'Error', err.message);
    }

    this.notification.error(this.extractMessage(errorMsg), err.title || 'Error');
  }

  private extractMessage(data: any): string {
    if (!data?.errors) return '';
    return data.errors.map((e: any) => `<p class="mb-1">${e.message}</p>`).join('');
  }

  private isApimUrl(request: HttpRequest<any>): boolean {
    return [
      environment.api,
      environment.authBaseURL,
      environment.basiq.apiBaseUrl,
      environment.analyticsPortalBaseUrl
    ].some(url => request.url.includes(url));
  }

  // -------------------------------------------------------------------------
  // MODAL HELPERS
  // -------------------------------------------------------------------------

  private openErrorModal(title: string, message: string): void {
    const modalRef = this.modal.open(ModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.data = {
      uilIconClass: 'uil-exclamation-triangle',
      title,
      content: message,
      buttons: [{ text: 'Acknowledge', class: 'btn-primary w-230 ml-1', value: 'true' }]
    };

    modalRef.closed.pipe(take(1)).subscribe(() => {
      this.router.navigate(['payment-plan-summary/pending']);
    });
  }

  private openSessionTimedOutDialog(): void {
    // authentication error
    // logout
    const modalRef = this.modal.open(ModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.data = {
      title: 'Authentication Error',
      closeBtn: true,
      content: 'Session timed out, please log in again',
      buttons: [{ text: 'Log in', class: 'btn-primary', value: 'ok' }]
    };

    modalRef.closed.pipe(take(1)).subscribe(() => {
      this.modal.dismissAll();
      sessionStorage.clear();
      this.router.navigateByUrl('/login');
    });
  }
}
